import {forwardRef, ReactNode, useImperativeHandle, useLayoutEffect, useRef} from 'react';
import cn from 'classnames';

import {Service} from '@zzng/common/constants/service';
import {addPaddingOnGroupEmpty} from '@zzng/common/markup/addPaddingOnGroupEmpty';

import Footer, {FooterProps} from '@/shared/components/Footer';
import Header, {HeaderProps} from '@/shared/components/Header';
import {STATUS_BAR_COLOR} from '@/shared/constants/statusBarColor';
import useScrollBlock from '@/shared/hooks/useScrollBlock';
import {StatusBarColorProps, useStatusBarColor} from '@/shared/hooks/useStatusBarColor';
import useTiara from '@/shared/hooks/useTiara';
import {useHalfviewStore} from '@/shared/stores/halfview';

export const enum PageDescription {
  ISSUE = '발급하기 페이지',
  SUBMIT = '제출하기 페이지',
  EVENT_BENEFIT = '디지털 카드 혜텍',
  GUIDE = '안내 페이지',
  ACCOUNT = '카카오 계정',
  HOME = '홈',
  DETAIL = '카드 상세'
}

type TiaraProps = {
  section?: Service;
  pageName: string;
  pageAction?: string;
  customProps?: {[key: string]: string};
  initCustomProps?: {[key: string]: string};
};

type Props = {
  children?: ReactNode;
  pageDescription?: PageDescription;
  className?: string;
  articleClassName?: string;
  showLogo?: boolean;
  title?: string;
  statusBarColor?: StatusBarColorProps;
  headerProps?: Omit<HeaderProps, 'trackEvent'>;
  tiara: false | TiaraProps;
  footerProps?: Omit<FooterProps, 'trackEvent'>;
};

const PageLayout = forwardRef<HTMLDivElement, Props>(
  (
    {
      children,
      title,
      pageDescription,
      className,
      articleClassName,
      footerProps,
      showLogo,
      statusBarColor = {default: STATUS_BAR_COLOR.default},
      headerProps,
      tiara
    },
    ref
  ) => {
    const {open} = useHalfviewStore();
    const {trackEvent, trackPage} = useTiara({
      section: tiara ? tiara.section : undefined,
      pageName: tiara ? tiara.pageName : undefined,
      customProps: tiara ? tiara.initCustomProps : undefined
    });

    const containerRef = useRef<HTMLDivElement | null>(null);
    useImperativeHandle(ref, () => containerRef.current as HTMLDivElement);

    useLayoutEffect(() => {
      addPaddingOnGroupEmpty();

      if (!tiara) return;

      if (tiara.pageAction) {
        const {pageAction, customProps} = tiara;
        trackPage({action: pageAction, ...(customProps && {customProps})});
      }
    }, []);

    useStatusBarColor(statusBarColor);
    useScrollBlock(open, containerRef);

    return (
      <div ref={containerRef} className={cn('container-doc', className)}>
        <header className="doc-header">
          <h1 className="doc-title screen_out">{title}</h1> {showLogo && <span className="img_logo">Kakao</span>}
        </header>
        <main className="doc-main">
          <article className={cn('content-article', articleClassName)}>
            {pageDescription && <h2 className="screen_out">{pageDescription}</h2>}
            <Header {...headerProps} trackEvent={trackEvent} />
            {children}
          </article>
        </main>
        {!!footerProps && <Footer {...footerProps} trackEvent={trackEvent} />}
      </div>
    );
  }
);

PageLayout.displayName = 'PageLayout';
export default PageLayout;
